// RestaurantOrderState.js
// Builds the initial (hardcoded, not backend-fetched) stageFields object for
// RestaurantOrder.js, matching the real process definition 1:1.
//
// This is still 100% static/hardcoded - mkField() is just a factory to avoid
// writing 79 near-identical object literals by hand. No data is fetched or
// inferred at runtime; every field here mirrors a specific fieldName/fieldType
// from the process definition API response.

import { F } from './RestaurantOrderFields';
import { ORDER_TYPES } from '../../Layout/POSSidebar';

export const INPUT = 1;
export const DROPDOWN = 2;
export const MULTIDROPDOWN = 3;
export const CHECKBOX = 4;
export const RADIO = 5;
export const TEXTAREA = 6;
export const DATETIMEPICKER = 7;
export const DATEPICKER = 8;
export const CONTAINER = 9;
export const SWITCH = 10;
export const FILE = 11;

const defaultValueFor = (fieldType) => {
    switch (fieldType) {
        case DROPDOWN: return null;
        case MULTIDROPDOWN: return [];
        case DATEPICKER:
        case DATETIMEPICKER: return null;
        case SWITCH: return false;
        default: return '';
    }
};

function mkField(fieldName, label, fieldType, parentFieldId, opts = {}) {
    return {
        FieldName: fieldName.replace('field_', ''),
        FieldLabel: label,
        FieldValue: opts.value !== undefined ? opts.value : defaultValueFor(fieldType),
        FieldType: fieldType,
        DataType: opts.dataType || 'string',
        ParentFieldId: parentFieldId,
        ChildRowNmbr: opts.childRowNmbr || 1,
        type: opts.containerType || 'attributes',
        FieldId: '',
        Required: !!opts.required,
        ContainerFields: opts.containerFields !== undefined ? opts.containerFields : null,
    };
}

// ── Single-row container template builders ────────────────────────────────
// (Table Information, Customer Information, Delivery Address, Delivery
//  Information, Bill Summary, Payment Information — each exactly one row,
//  no add/remove UI needed.)
//
// NOTE: written out per-field (not looped) to stay consistent with the
// "no dynamic rendering" approach used throughout this file.

export function buildTableInformationRow() {
    const parent = F.TABLE_INFO.replace('field_', '');
    return {
        [F.TABLE_NUMBER]: mkField(F.TABLE_NUMBER, 'Table Number', INPUT, parent),
        [F.TABLE_SUB_CODE]: mkField(F.TABLE_SUB_CODE, 'Table Sub Code', INPUT, parent),
        [F.NO_OF_GUESTS]: mkField(F.NO_OF_GUESTS, 'No. Of Guests', INPUT, parent, { dataType: 'int' }),
        [F.ADULT_COUNT]: mkField(F.ADULT_COUNT, 'Adult Count', INPUT, parent, { dataType: 'int' }),
        [F.CHILD_COUNT]: mkField(F.CHILD_COUNT, 'Child Count', INPUT, parent, { dataType: 'int' }),
        [F.RESERVATION_NO]: mkField(F.RESERVATION_NO, 'Reservation No', INPUT, parent),
        [F.TABLE_STATUS]: mkField(F.TABLE_STATUS, 'Table Status', DROPDOWN, parent),
    };
}

export function buildCustomerInformationRow() {
    const parent = F.CUSTOMER_INFO.replace('field_', '');
    return {
        [F.CUST_NAME]: mkField(F.CUST_NAME, 'Name', INPUT, parent),
        [F.CUST_MOBILE]: mkField(F.CUST_MOBILE, 'Mobile No.', INPUT, parent),
        [F.CUST_MEMBERSHIP_NO]: mkField(F.CUST_MEMBERSHIP_NO, 'Membership No', INPUT, parent),
        [F.CUST_GST]: mkField(F.CUST_GST, 'GST Number', INPUT, parent),
        [F.CUST_REMARKS]: mkField(F.CUST_REMARKS, 'Remarks', TEXTAREA, parent),
    };
}

export function buildDeliveryAddressRow() {
    const parent = F.DELIVERY_ADDRESS.replace('field_', '');
    return {
        [F.DEL_ADDRESS]: mkField(F.DEL_ADDRESS, 'Address', TEXTAREA, parent),
        [F.DEL_LANDMARK]: mkField(F.DEL_LANDMARK, 'LandMark', INPUT, parent),
        [F.DEL_GOOGLE_LOC]: mkField(F.DEL_GOOGLE_LOC, 'Google Location', INPUT, parent),
        [F.DEL_BUILDING]: mkField(F.DEL_BUILDING, 'Building', INPUT, parent),
        [F.DEL_FLOOR]: mkField(F.DEL_FLOOR, 'Floor', INPUT, parent),
        [F.DEL_DOOR_NO]: mkField(F.DEL_DOOR_NO, 'Door No', INPUT, parent),
    };
}

export function buildDeliveryInformationRow() {
    const parent = F.DELIVERY_INFO.replace('field_', '');
    return {
        [F.DEL_BOY]: mkField(F.DEL_BOY, 'Delivery Boy', DROPDOWN, parent),
        [F.DEL_CHARGE]: mkField(F.DEL_CHARGE, 'Delivery Charge', INPUT, parent, { dataType: 'int' }),
        [F.DEL_EXPECTED_TIME]: mkField(F.DEL_EXPECTED_TIME, 'Expected Delivery Time', INPUT, parent),
        [F.DEL_DISTANCE]: mkField(F.DEL_DISTANCE, 'Distance', INPUT, parent),
        [F.DEL_STATUS]: mkField(F.DEL_STATUS, 'Delivery Status', DROPDOWN, parent),
        [F.DEL_OTP]: mkField(F.DEL_OTP, 'OTP', INPUT, parent),
        [F.DEL_INSTRUCTIONS]: mkField(F.DEL_INSTRUCTIONS, 'Delivery Instructions', TEXTAREA, parent),
    };
}

export function buildBillSummaryRow() {
    const parent = F.BILL_SUMMARY.replace('field_', '');
    return {
        [F.BILL_GROSS]: mkField(F.BILL_GROSS, 'Gross Amount', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_ITEM_DISCOUNT]: mkField(F.BILL_ITEM_DISCOUNT, 'Item Discount', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_DISCOUNT]: mkField(F.BILL_DISCOUNT, 'Bill Discount', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_TAX]: mkField(F.BILL_TAX, 'Tax', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_SERVICE_CHG]: mkField(F.BILL_SERVICE_CHG, 'Service Charge', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_PACKING_CHG]: mkField(F.BILL_PACKING_CHG, 'Packing Charge', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_DELIVERY_CHG]: mkField(F.BILL_DELIVERY_CHG, 'Delivery Charge', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_ROUND_OFF]: mkField(F.BILL_ROUND_OFF, 'Round Off', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.BILL_GRAND_TOTAL]: mkField(F.BILL_GRAND_TOTAL, 'Grand Total', INPUT, parent, { dataType: 'int', value: 0 }),
    };
}

export function buildPaymentInformationRow() {
    const parent = F.PAYMENT_INFO.replace('field_', '');
    return {
        [F.PAY_TYPE]: mkField(F.PAY_TYPE, 'Payment Type', DROPDOWN, parent),
        [F.PAY_AMOUNT_TENDERED]: mkField(F.PAY_AMOUNT_TENDERED, 'Amount Tendered', INPUT, parent, { dataType: 'int' }),
        [F.PAY_CHANGE_RETURN]: mkField(F.PAY_CHANGE_RETURN, 'Change Return', INPUT, parent, { dataType: 'int' }),
        [F.PAY_TRANSACTION_NO]: mkField(F.PAY_TRANSACTION_NO, 'Transaction No', INPUT, parent),
        [F.PAY_CARD_NO]: mkField(F.PAY_CARD_NO, 'Card No', INPUT, parent),
        [F.PAY_BANK]: mkField(F.PAY_BANK, 'Bank', INPUT, parent),
        [F.PAY_APPROVAL_NO]: mkField(F.PAY_APPROVAL_NO, 'Approval No', INPUT, parent),
        [F.PAY_STATUS]: mkField(F.PAY_STATUS, 'Payment Status', INPUT, parent),
    };
}

// ── Repeating container row builders (Menu Items / KOT Information) ───────

export function buildMenuItemRow() {
    const parent = F.MENU_ITEMS.replace('field_', '');
    return {
        [F.MI_ITEM]: mkField(F.MI_ITEM, 'Menu Item', DROPDOWN, parent),
        [F.MI_QTY]: mkField(F.MI_QTY, 'Qty', INPUT, parent, { dataType: 'int', value: 1 }),
        [F.MI_UOM]: mkField(F.MI_UOM, 'UOM', DROPDOWN, parent),
        [F.MI_RATE]: mkField(F.MI_RATE, 'Rate', INPUT, parent, { dataType: 'int' }),
        [F.MI_DISCOUNT_PCT]: mkField(F.MI_DISCOUNT_PCT, 'Discount Percentage', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_TAX_PCT]: mkField(F.MI_TAX_PCT, 'Tax Percentage', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_NET_AMOUNT]: mkField(F.MI_NET_AMOUNT, 'Net Amount', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_KITCHEN]: mkField(F.MI_KITCHEN, 'Kitchen', DROPDOWN, parent),
        [F.MI_SPECIAL_INSTR]: mkField(F.MI_SPECIAL_INSTR, 'Special Instruction', TEXTAREA, parent),
        [F.MI_KOT_STATUS]: mkField(F.MI_KOT_STATUS, 'KOT Status', DROPDOWN, parent, { value: 'Pending' }),
        [F.MI_SERVED_QTY]: mkField(F.MI_SERVED_QTY, 'Served Qty', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_CANCELLED_QTY]: mkField(F.MI_CANCELLED_QTY, 'Cancelled Qty', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_PREPARED_QTY]: mkField(F.MI_PREPARED_QTY, 'Prepared Qty', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_BALANCE_QTY]: mkField(F.MI_BALANCE_QTY, 'Balance Qty', INPUT, parent, { dataType: 'int', value: 0 }),
        [F.MI_MODIFIER_SEL]: mkField(F.MI_MODIFIER_SEL, 'Modifier Selection', MULTIDROPDOWN, parent),
        [F.MI_ORDERED_BY]: mkField(F.MI_ORDERED_BY, 'Ordered By', INPUT, parent),
    };
}

export function buildKotInfoRow() {
    const parent = F.KOT_INFO.replace('field_', '');
    return {
        [F.KOT_NUMBER]: mkField(F.KOT_NUMBER, 'KOT Number', INPUT, parent),
        [F.KOT_KITCHEN]: mkField(F.KOT_KITCHEN, 'Kitchen', DROPDOWN, parent),
        [F.KOT_MENU_ITEM]: mkField(F.KOT_MENU_ITEM, 'Menu Item', DROPDOWN, parent),
        [F.KOT_STATUS]: mkField(F.KOT_STATUS, 'Status', DROPDOWN, parent, { value: 'Sent' }),
    };
}

// ── Full stage 0 field set (top-level flat fields + 8 containers) ─────────

export function buildStageFields() {
    return {
        [F.REQUESTED_FOR]: mkField(F.REQUESTED_FOR, 'Requested For', DROPDOWN, '000', { required: true }),
        [F.REQUEST_DATE]: mkField(F.REQUEST_DATE, 'Request Date', DATETIMEPICKER, '000', { required: true, value: new Date() }),
        [F.ORDER_NUMBER]: mkField(F.ORDER_NUMBER, 'Order Number', INPUT, '000', { required: true }),
        [F.BRANCH]: mkField(F.BRANCH, 'Branch', DROPDOWN, '000', { required: true }),
        [F.ORDER_TYPE]: mkField(F.ORDER_TYPE, 'Order Type', DROPDOWN, '000', { value: ORDER_TYPES.DINE_IN }),
        [F.ORDER_STATUS]: mkField(F.ORDER_STATUS, 'Order Status', DROPDOWN, '000', { value: 'Draft' }),
        [F.WAITER]: mkField(F.WAITER, 'Waiter', DROPDOWN, '000'),
        [F.CASHIER]: mkField(F.CASHIER, 'Cashier', DROPDOWN, '000'),
        [F.SHIFT]: mkField(F.SHIFT, 'Shift', DROPDOWN, '000'),
        [F.TOKEN_NUMBER]: mkField(F.TOKEN_NUMBER, 'Token Number', INPUT, '000'),
        [F.DELIVERY_DATE]: mkField(F.DELIVERY_DATE, 'Delivery Date', DATEPICKER, '000'),
        [F.EXPECTED_DELIVERY_TIME]: mkField(F.EXPECTED_DELIVERY_TIME, 'Expected Delivery Time', INPUT, '000'),

        [F.TABLE_INFO]: mkField(F.TABLE_INFO, 'Table Information', CONTAINER, '000', {
            containerFields: [buildTableInformationRow()],
        }),
        [F.CUSTOMER_INFO]: mkField(F.CUSTOMER_INFO, 'Customer Information', CONTAINER, '000', {
            containerFields: [buildCustomerInformationRow()],
        }),
        [F.DELIVERY_ADDRESS]: mkField(F.DELIVERY_ADDRESS, 'Delivery Address', CONTAINER, '000', {
            containerFields: [buildDeliveryAddressRow()],
        }),
        [F.DELIVERY_INFO]: mkField(F.DELIVERY_INFO, 'Delivery Information', CONTAINER, '000', {
            containerFields: [buildDeliveryInformationRow()],
        }),
        [F.MENU_ITEMS]: mkField(F.MENU_ITEMS, 'Menu Items', CONTAINER, '000', {
            containerFields: [], // starts empty - rows added as items are added to cart
        }),
        [F.KOT_INFO]: mkField(F.KOT_INFO, 'KOT Information', CONTAINER, '000', {
            containerFields: [], // starts empty - one row appended per "Send to Kitchen"
        }),
        [F.BILL_SUMMARY]: mkField(F.BILL_SUMMARY, 'Bill Summary', CONTAINER, '000', {
            containerFields: [buildBillSummaryRow()],
        }),
        [F.PAYMENT_INFO]: mkField(F.PAYMENT_INFO, 'Payment Information', CONTAINER, '000', {
            containerFields: [buildPaymentInformationRow()],
        }),
    };
}


export function buildPaymentRow() {

    const parent = F.PAYMENT_INFO.replace('field_', '');

    return {

        [F.PAY_TYPE]: mkField(
            F.PAY_TYPE,
            'Payment Type',
            DROPDOWN,
            parent
        ),

        [F.PAY_AMOUNT_TENDERED]: mkField(
            F.PAY_AMOUNT_TENDERED,
            'Amount Tendered',
            INPUT,
            parent,
            {
                dataType: 'int',
                value: 0
            }
        ),

        [F.PAY_CHANGE_RETURN]: mkField(
            F.PAY_CHANGE_RETURN,
            'Change Return',
            INPUT,
            parent,
            {
                dataType: 'int',
                value: 0
            }
        ),

        [F.PAY_TRANSACTION_NO]: mkField(
            F.PAY_TRANSACTION_NO,
            'Transaction No',
            INPUT,
            parent
        ),

        [F.PAY_CARD_NO]: mkField(
            F.PAY_CARD_NO,
            'Card No',
            INPUT,
            parent
        ),

        [F.PAY_BANK]: mkField(
            F.PAY_BANK,
            'Bank',
            INPUT,
            parent
        ),

        [F.PAY_APPROVAL_NO]: mkField(
            F.PAY_APPROVAL_NO,
            'Approval No',
            INPUT,
            parent
        ),

        [F.PAY_STATUS]: mkField(
            F.PAY_STATUS,
            'Payment Status',
            DROPDOWN,
            parent,
            {
                value: 'Paid'
            }
        )

    };

}
